'use client'

import { useEffect } from 'react'
import { getTrackingService } from '@/services/tracking/TrackingInstance'

// TODO refactor to use enum like types for events in components like Teaser.state
export enum TriggerType {
  CLICK = 'click',
  VISIBILITY = 'visibility',
  SCROLL = 'scroll',
}

export type TrackingEventDescriptor = {
  commandType: string
  payload?: Record<string, any>
  trigger: string
  threshold?: number | number[]
  rootMargin?: string
}

type ClientBinderProps = {
  events: TrackingEventDescriptor[]
  targetId: string
  href?: string
}

export default function ClientBinder({
  events,
  targetId,
  href,
}: ClientBinderProps) {
  useEffect(() => {
    const el = document.getElementById(targetId)
    if (!el) return
    const svc = getTrackingService()

    let observer: IntersectionObserver | null = null
    const ioEvents = events.filter(
      (e) => e.trigger !== TriggerType.CLICK.toString(),
    )
    if (ioEvents.length) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return
            ioEvents.forEach((ev) => {
              if (ev.trigger === TriggerType.VISIBILITY) {
                svc.addAndExecuteCommand(ev.commandType, ev.payload)
                observer!.unobserve(entry.target)
              } else {
                svc.addAndExecuteCommand(ev.commandType, {
                  ...ev.payload,
                  scrollRatio: entry.intersectionRatio,
                })
              }
            })
          })
        },
        {
          threshold: ioEvents.flatMap((e) =>
            Array.isArray(e.threshold) ? e.threshold : [e.threshold ?? 0],
          ),
          rootMargin: ioEvents.find((e) => e.rootMargin)?.rootMargin ?? '0px',
        },
      )
      observer.observe(el)
    }

    const clickEvents = events.filter(
      (e) => e.trigger === TriggerType.CLICK.toString(),
    )
    if (clickEvents.length) {
      el.style.cursor = 'pointer'
      const onClick = (e: MouseEvent) => {
        if (href || el.tagName.toLowerCase() === 'a') {
          e.preventDefault()
        }
        clickEvents.forEach((ev) =>
          svc.addAndExecuteCommand(ev.commandType, ev.payload),
        )
        if (href || el.tagName.toLowerCase() === 'a') {
          window.location.href = href ?? (el as HTMLAnchorElement).href
        }
      }
      el.addEventListener('click', onClick)
      return () => {
        el.removeEventListener('click', onClick)
        if (observer) observer.disconnect()
      }
    }

    return () => {
      if (observer) observer.disconnect()
    }
  }, [events, href, targetId])

  return null
}
